import type { CSSProperties, ReactNode } from "react";

// CallerIDNotice — deutlicher visueller Marker für den Caller-ID-Hinweis.
// Ersetzt den kursiven Helper-Text an Stellen, wo eine Telefonnummer für
// die Erzähler-Identifikation eingegeben wird. Drei Slots:
//
//   <CallerIDNotice
//     who="Oma"                           // optional, sonst neutral
//     vitanaraNumber="0 4103 7044937"
//   />
//
// Die Box ist absichtlich auffällig (oxblood-Border-Akzent + warmer Paper-
// Hintergrund + Telefon-Icon-Badge) — kritischer Constraint, der nicht
// überlesen werden darf.

export default function CallerIDNotice({
  who,
  vitanaraNumber = "0 4103 7044937",
  style,
  compact = false,
}: {
  who?: string; // z.B. "Oma", "Opa", "Mama", "Sie selbst"
  vitanaraNumber?: string;
  style?: CSSProperties;
  compact?: boolean;
}) {
  const person = who || "Die erzählende Person";
  const possessive = who ? ihr(who) : "ihrer/seiner";

  return (
    <aside
      role="note"
      style={{
        background: "var(--paper-50)",
        border: "1px solid rgba(107,32,24,0.18)",
        borderLeft: "3px solid var(--oxblood)",
        borderRadius: 10,
        padding: compact ? "12px 14px" : "16px 18px",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        margin: "8px 0 16px",
        ...style,
      }}
    >
      {/* Icon badge */}
      <div
        aria-hidden
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "rgba(107,32,24,0.10)",
          color: "var(--oxblood)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
        </svg>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="t-caps"
          style={{
            fontSize: 10,
            color: "var(--oxblood)",
            marginBottom: 4,
          }}
        >
          So funktioniert die Erkennung
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 14.5,
            lineHeight: 1.5,
            color: "var(--ink)",
          }}
        >
          {person} ruft <strong style={{ fontWeight: 600 }}>unsere Festnetznummer</strong>{" "}
          <span
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              color: "var(--oxblood)",
              whiteSpace: "nowrap",
            }}
          >
            {vitanaraNumber}
          </span>{" "}
          an. Konrad und Ida wissen schon an der{" "}
          <strong style={{ fontWeight: 600 }}>Absender-Rufnummer</strong>, wer dran ist.
        </div>

        {!compact && (
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              lineHeight: 1.45,
              color: "var(--ink-soft)",
            }}
          >
            Tragen Sie deshalb genau die Nummer ein, von der {who ? who : "die Person"} anrufen wird.
            Anrufe von einem fremden Apparat (Nachbar, Mobiltelefon eines Enkels,
            aus dem Urlaub) können wir {possessive} Account nicht zuordnen — weitere
            Nummern lassen sich später ergänzen.
          </div>
        )}
      </div>
    </aside>
  );
}

function ihr(who: string): string {
  const lower = who.trim().toLowerCase();
  // crude German possessive picker for the most common gift recipients
  if (
    lower.startsWith("opa") ||
    lower.startsWith("papa") ||
    lower.startsWith("vater") ||
    lower.startsWith("onkel") ||
    lower.endsWith("er")
  ) {
    return "seinem";
  }
  return "ihrem";
}

// ─── Optional inline variant ──────────────────────────────────────────────
// Für Stellen, wo die volle Box zu groß ist (z.B. Admin-Tabellen-Edits).
// Renders nur die wichtige Zeile mit oxblood-Icon links.

export function CallerIDNoticeInline({
  vitanaraNumber = "0 4103 7044937",
  children,
}: {
  vitanaraNumber?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role="note"
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        padding: "8px 10px",
        background: "rgba(107,32,24,0.05)",
        borderLeft: "2px solid var(--oxblood)",
        borderRadius: 4,
        fontFamily: "var(--f-body)",
        fontSize: 13,
        lineHeight: 1.5,
        color: "var(--ink-soft)",
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--oxblood)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ marginTop: 3, flexShrink: 0 }}
        aria-hidden
      >
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
      </svg>
      <span>
        {children || (
          <>
            Erkennung über die Absender-Rufnummer. Vitanara-Festnetz:{" "}
            <strong style={{ color: "var(--ink)", fontWeight: 600 }}>
              {vitanaraNumber}
            </strong>
            .
          </>
        )}
      </span>
    </div>
  );
}
